import * as React from 'react';
import { useTranslation } from 'react-i18next';
import SimpleModal from 'ui/Modals/SimpleModal';
import Typography from '@mui/material/Typography';
import { downloadFile } from 'helpers/helpers';
import { trackEvent } from 'helpers/trackerHelpers';
import Loading from 'components/Loading';
import Button from '@mui/material/Button';
import { getUrlAsPDF, EMPTY_ARGUMENT_ERROR } from 'helpers/pdf';
import WidgetTruncatedWarningContext from 'components/Contexts/WidgetTruncatedWarningContext';
import { Alert } from '@mui/material';

type DownloadButtonProps = {
    onClick: () => void;
    disabled: boolean;
};

const DownloadButton = ({ disabled, onClick }: DownloadButtonProps) => {
    const { t } = useTranslation();
    return (
        <Button variant="contained" disabled={disabled} onClick={onClick}>
            {t('common.download')}
        </Button>
    );
};

type Props = {
    open: boolean;
    url: string;
    downloadTrackingEvent?: {
        name: string;
        payload?: Record<string, number | string | null>;
    };
    onClose: () => void;
    title: string;
    hasDownloadButton?: boolean;
    downloadTitle?: string;
    onSuccessText?: React.ReactElement | null;
    shouldDisplayFixedWidthText?: boolean;
};

const PdfPreviewModal = ({
    open,
    url,
    downloadTrackingEvent = undefined,
    onClose,
    title,
    hasDownloadButton = false,
    downloadTitle = 'manty pdf',
    onSuccessText = null,
    shouldDisplayFixedWidthText = false,
}: Props): React.ReactElement => {
    const [pdfURL, setpdfURL] = React.useState<string>('');
    const [isError, setIsError] = React.useState<boolean>(false);
    const [errorMessage, setErrorMessage] = React.useState<string>(
        'error.pdfGenerationError',
    );
    const { shouldShowWidgetTruncatedWarning: isWidgetTruncated } =
        React.useContext(WidgetTruncatedWarningContext);

    React.useEffect(() => {
        setIsError(false);
        if (open) {
            getUrlAsPDF(url)
                .then((newURL) => {
                    setpdfURL(newURL);
                })
                .catch((e) => {
                    if (e?.cause === EMPTY_ARGUMENT_ERROR) {
                        setErrorMessage(e?.cause);
                    }
                    setIsError(true);
                });
        }
    }, [open, url]);

    const { t } = useTranslation();

    const additionalButtons = [];

    if (hasDownloadButton) {
        const downloadButtonOnClick = () => {
            downloadFile(pdfURL, downloadTitle, 'pdf');
            if (downloadTrackingEvent !== undefined) {
                const { name: eventName, payload: eventPayload } =
                    downloadTrackingEvent;
                trackEvent(eventName, eventPayload);
            }
            setpdfURL('');
            onClose();
        };

        additionalButtons.push(
            <DownloadButton
                key="download-button"
                onClick={downloadButtonOnClick}
                disabled={pdfURL === ''}
            />,
        );
    }

    const fixedWidthText = shouldDisplayFixedWidthText && (
        <Alert severity="info" sx={{ margin: '12px 0' }}>
            {t('sendDashboard.fullWidthDashboard')}
        </Alert>
    );

    return (
        <SimpleModal
            open={open}
            onClose={() => {
                window.URL.revokeObjectURL(pdfURL);
                setpdfURL('');
                onClose();
            }}
            maxWidth="md"
            additionalButtons={additionalButtons}
            fullHeight={shouldDisplayFixedWidthText}
        >
            <div
                style={{
                    width: '100%',
                    height: shouldDisplayFixedWidthText ? '85vh' : '65vh',
                }}
            >
                {pdfURL ? (
                    <div style={{ height: '100%', border: 'none' }}>
                        {fixedWidthText}
                        <iframe
                            title="PDF"
                            width="100%"
                            height={shouldDisplayFixedWidthText ? '75%' : '90%'}
                            src={pdfURL}
                        />
                        {onSuccessText}
                    </div>
                ) : (
                    <div style={{ height: '100%' }}>
                        <Typography variant="h4">{title}</Typography>
                        <Typography>
                            {t('sendDashboard.timeOfPreview')}
                        </Typography>
                        {!isError ? (
                            <div>
                                {isWidgetTruncated && (
                                    <Typography
                                        variant="body1SemiBold"
                                        sx={{ mt: '1rem' }}
                                    >
                                        {t('widget.widgetTruncatedWarning')}
                                    </Typography>
                                )}
                                {fixedWidthText}
                                <Loading />
                            </div>
                        ) : (
                            <div
                                style={{
                                    width: '100%',
                                    height: '100%',
                                    display: 'flex',
                                    alignItems: 'center',
                                    justifyContent: 'center',
                                }}
                            >
                                <Typography>
                                    {t(errorMessage, { ns: 'backend' })}
                                </Typography>
                            </div>
                        )}
                    </div>
                )}
            </div>
        </SimpleModal>
    );
};

export default PdfPreviewModal;
